"use strict";
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  emptyValidCoordinateSpace,
  makeCoordinateSpace,
  makeIdentityTransform
} from "#src/coordinate_transform.js";
import { uncancelableToken } from "#src/util/cancellation.js";
import {
  applyCompletionOffset,
  getPrefixMatchesWithDescriptions
} from "#src/util/completion.js";
import { RefCounted } from "#src/util/disposable.js";
import { createIdentity } from "#src/util/matrix.js";
export class RedirectError extends Error {
  constructor(redirectTarget) {
    super(`Redirected to: ${redirectTarget}`);
    this.redirectTarget = redirectTarget;
  }
}
export function findSourceGroupBasedOnSeparator(path, separator) {
  if (separator === void 0) {
    if (path.indexOf("/") === -1) {
      separator = ":";
    } else {
      separator = "/";
    }
  }
  const index = path.lastIndexOf(separator);
  if (index === -1) {
    return 0;
  }
  return index + 1;
}
export function suggestLayerNameBasedOnSeparator(path, separator) {
  const groupIndex = findSourceGroupBasedOnSeparator(path, separator);
  return path.substring(groupIndex);
}
export var LocalDataSource = /* @__PURE__ */ ((LocalDataSource2) => {
  LocalDataSource2[LocalDataSource2["annotations"] = 0] = "annotations";
  LocalDataSource2[LocalDataSource2["equivalences"] = 1] = "equivalences";
  return LocalDataSource2;
})(LocalDataSource || {});
export function makeEmptyDataSourceSpecification() {
  return {
    url: "",
    transform: void 0,
    enableDefaultSubsources: true,
    subsources: /* @__PURE__ */ new Map()
  };
}
export class DataSourceProvider extends RefCounted {
  normalizeUrl(options) {
    return options.url;
  }
  convertLegacyUrl(options) {
    return options.url;
  }
  async completeUrl(options) {
    options;
    throw null;
  }
}
export const localAnnotationsUrl = "local://annotations";
export const localEquivalencesUrl = "local://equivalences";
class LocalDataSourceProvider extends DataSourceProvider {
  get description() {
    return "Local in-memory";
  }
  async get(options) {
    switch (options.url) {
      case localAnnotationsUrl: {
        const { transform } = options;
        let modelTransform;
        if (transform === void 0) {
          const baseSpace = options.globalCoordinateSpace.value;
          const { rank, names, scales, units } = baseSpace;
          const inputSpace = makeCoordinateSpace({
            rank,
            scales,
            units,
            names: names.map((_, i) => `${i}`)
          });
          const outputSpace = makeCoordinateSpace({
            rank,
            scales,
            units,
            names
          });
          modelTransform = {
            rank,
            sourceRank: rank,
            inputSpace,
            outputSpace,
            transform: createIdentity(Float64Array, rank + 1)
          };
        } else {
          modelTransform = makeIdentityTransform(emptyValidCoordinateSpace);
        }
        return {
          modelTransform,
          canChangeModelSpaceRank: true,
          subsources: [
            {
              id: "default",
              default: true,
              subsource: {
                local: 0 /* annotations */
              }
            }
          ]
        };
      }
      case localEquivalencesUrl: {
        return {
          modelTransform: makeIdentityTransform(emptyValidCoordinateSpace),
          canChangeModelSpaceRank: false,
          subsources: [
            {
              id: "default",
              default: true,
              subsource: {
                local: 1 /* equivalences */
              }
            }
          ]
        };
      }
    }
    throw new Error("Invalid local data source URL");
  }
  async completeUrl(options) {
    return {
      offset: 0,
      completions: getPrefixMatchesWithDescriptions(
        options.providerUrl,
        [
          {
            value: "annotations",
            description: "Annotations stored in the JSON state"
          },
          {
            value: "equivalences",
            description: "Segmentation equivalence graph stored in the JSON state"
          }
        ],
        (x) => x.value,
        (x) => x.description
      )
    };
  }
}
const protocolPattern = /^(?:([a-zA-Z][a-zA-Z0-9-+_]*):\/\/)?(.*)$/;
export class DataSourceProviderRegistry extends RefCounted {
  constructor(credentialsManager) {
    super();
    this.credentialsManager = credentialsManager;
    this.dataSources = /* @__PURE__ */ new Map([
      ["local", new LocalDataSourceProvider()]
    ]);
  }
  register(name, dataSource) {
    this.dataSources.set(name, this.registerDisposer(dataSource));
  }
  getProvider(url) {
    const m = url.match(protocolPattern);
    if (m === null || m[1] === void 0) {
      throw new Error(
        `Data source URL must have the form "<protocol>://<path>".`
      );
    }
    const [, providerProtocol, providerUrl] = m;
    const factory = this.dataSources.get(providerProtocol);
    if (factory === void 0) {
      throw new Error(
        `Unsupported data source: ${JSON.stringify(providerProtocol)}.`
      );
    }
    return [factory, providerUrl, providerProtocol];
  }
  async get(options) {
    const redirectLog = /* @__PURE__ */ new Set();
    const { cancellationToken = uncancelableToken } = options;
    let url = options.url;
    while (true) {
      const [provider, providerUrl, providerProtocol] = this.getProvider(
        options.url
      );
      redirectLog.add(options.url);
      try {
        return provider.get({
          ...options,
          url,
          providerProtocol,
          providerUrl,
          registry: this,
          cancellationToken,
          credentialsManager: this.credentialsManager
        });
      } catch (e) {
        if (e instanceof RedirectError) {
          const redirect = e.redirectTarget;
          if (redirectLog.has(redirect)) {
            throw Error(
              `Layer source redirection contains loop: ${JSON.stringify(
                Array.from(redirectLog)
              )}`
            );
          }
          if (redirectLog.size >= 10) {
            throw Error(
              `Too many layer source redirections: ${JSON.stringify(
                Array.from(redirectLog)
              )}`
            );
          }
          url = redirect;
          continue;
        }
        throw e;
      }
    }
  }
  convertLegacyUrl(options) {
    try {
      const [provider, providerUrl, providerProtocol] = this.getProvider(
        options.url
      );
      return provider.convertLegacyUrl({
        ...options,
        providerUrl,
        providerProtocol,
        registry: this
      });
    } catch {
      return options.url;
    }
  }
  normalizeUrl(options) {
    try {
      const [provider, providerUrl, providerProtocol] = this.getProvider(
        options.url
      );
      return provider.normalizeUrl({
        ...options,
        providerUrl,
        providerProtocol,
        registry: this
      });
    } catch {
      return options.url;
    }
  }
  async completeUrl(options) {
    const { url, cancellationToken = uncancelableToken } = options;
    const protocolMatch = url.match(protocolPattern);
    const protocol = protocolMatch[1];
    if (protocol === void 0) {
      return Promise.resolve({
        offset: 0,
        completions: getPrefixMatchesWithDescriptions(
          url,
          this.dataSources,
          ([name]) => `${name}://`,
          ([, factory2]) => factory2.description
        )
      });
    }
    const factory = this.dataSources.get(protocol);
    if (factory !== void 0) {
      const completions = await factory.completeUrl({
        registry: this,
        url,
        providerUrl: protocolMatch[2],
        chunkManager: options.chunkManager,
        cancellationToken,
        credentialsManager: this.credentialsManager
      });
      return applyCompletionOffset(protocol.length + 3, completions);
    }
    throw null;
  }
  suggestLayerName(url) {
    let [dataSource, path] = this.getProvider(url);
    if (path.endsWith("/")) {
      path = path.substring(0, path.length - 1);
    }
    const suggestor = dataSource.suggestLayerName;
    if (suggestor !== void 0) {
      return suggestor(path);
    }
    return suggestLayerNameBasedOnSeparator(path);
  }
  findSourceGroup(url) {
    const [dataSource, path, dataSourceName] = this.getProvider(url);
    const helper = dataSource.findSourceGroup || findSourceGroupBasedOnSeparator;
    return helper(path) + dataSourceName.length + 3;
  }
}
